import { Vehicle } from "@/hooks/useVehicles";
import { ExpenseFormData, DEFAULT_EXPENSE_TYPES, DEFAULT_TAX_CATEGORIES } from "@/hooks/useExpenses";

export interface ExpenseImportData {
  expense_date: string;
  expense_name: string;
  car: string;
  expense_type: string;
  amount: number | null;
  mileage: number | null;
  paid_by: string;
  paid_to: string;
  notes: string;
}

export interface ParsedExpense {
  data: ExpenseFormData;
  carValue: string;
  needsNewVehicle: boolean;
  matchedVehicleId: string | null;
}

// Required columns for validation
const REQUIRED_COLUMNS = ["Date", "Expense", "Expense Type"];
const AMOUNT_OR_MILEAGE_COLUMNS = ["Amount", "Miles Driven"];

// Normalize a vehicle name for fuzzy matching
function normalizeName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_\s-]+/g, " ")
    .replace(/^\d{4}\s+/, "")
    .trim();
}

// CSV parsing with proper quote handling
export function parseCSV(csvContent: string): { headers: string[]; rows: string[][] } {
  const lines = csvContent.split("\n").filter((line) => line.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let current = "";
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === "," && !inQuotes) {
        result.push(current.trim());
        current = "";
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows = lines.slice(1).map((line) => parseLine(line));

  return { headers, rows };
}

// Validate CSV headers
export function validateHeaders(headers: string[]): { valid: boolean; missingColumns: string[] } {
  const normalizedHeaders = headers.map((h) => h.toLowerCase().trim());
  const missingColumns: string[] = [];

  // Check required columns
  for (const required of REQUIRED_COLUMNS) {
    if (!normalizedHeaders.includes(required.toLowerCase())) {
      missingColumns.push(required);
    }
  }

  // Check that at least one of Amount or Miles Driven is present
  const hasAmount = normalizedHeaders.includes("amount");
  const hasMileage = normalizedHeaders.includes("miles driven");
  
  if (!hasAmount && !hasMileage) {
    missingColumns.push("Amount or Miles Driven (at least one required)");
  }

  return {
    valid: missingColumns.length === 0,
    missingColumns,
  };
}

// Parse currency value
function parseCurrency(value: string): number | null {
  if (!value || value.trim() === "") return null;
  const cleaned = value.replace(/[$,]/g, "").trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

// Parse number value
function parseNumber(value: string): number | null {
  if (!value || value.trim() === "") return null;
  const cleaned = value.replace(/,/g, "").trim();
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? null : parsed;
}

// Parse date value (expecting various formats)
function parseDate(value: string): string | null {
  if (!value || value.trim() === "") return null;
  
  // Try parsing as ISO format first
  const isoDate = new Date(value);
  if (!isNaN(isoDate.getTime())) {
    return isoDate.toISOString().split("T")[0];
  }
  
  // Try MM/DD/YYYY format
  const parts = value.split("/");
  if (parts.length === 3) {
    const [month, day, year] = parts;
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0];
    }
  }
  
  return null;
}

// Parse a single CSV row into expense import data
export function parseExpenseRow(
  headers: string[],
  values: string[],
  vehicles: Vehicle[],
  expenseTypes: string[],
  paidByOptions: string[]
): ParsedExpense {
  const getVal = (colName: string): string => {
    const idx = headers.findIndex((h) => h.toLowerCase().trim() === colName.toLowerCase());
    return idx >= 0 ? values[idx] || "" : "";
  };

  const rawCarValue = getVal("Car").trim();
  const rawExpenseType = getVal("Expense Type").trim();
  const rawPaidBy = getVal("Paid By").trim();
  const rawAmount = parseCurrency(getVal("Amount"));
  const rawMileage = parseNumber(getVal("Miles Driven"));
  const rawDate = parseDate(getVal("Date"));

  // Match vehicle by nickname first, then by VIN
  let matchedVehicleId: string | null = null;
  let needsNewVehicle = false;

  if (rawCarValue) {
    // "Operations" is a reserved keyword — means no vehicle association
    if (rawCarValue.toLowerCase() === "operations") {
      matchedVehicleId = null;
      needsNewVehicle = false;
    } else {
      const normalizedCar = normalizeName(rawCarValue);

      // Try matching by nickname (normalized)
      const nicknameMatch = vehicles.find(
        (v) => v.name && normalizeName(v.name) === normalizedCar
      );
      
      if (nicknameMatch) {
        matchedVehicleId = nicknameMatch.id;
      } else {
        // Try matching by VIN (case-insensitive)
        const vinMatch = vehicles.find(
          (v) => v.vin?.toLowerCase() === rawCarValue.toLowerCase()
        );
        
        if (vinMatch) {
          matchedVehicleId = vinMatch.id;
        } else {
          // No match found - will need to create new vehicle
          needsNewVehicle = true;
        }
      }
    }
  }

  // Validate expense type - default to "Other" if not found
  const validExpenseType = expenseTypes.find(
    (t) => t.toLowerCase() === rawExpenseType.toLowerCase()
  );
  const expenseType = validExpenseType || "Other";

  // Validate paid_by - default to empty if not found
  const validPaidBy = paidByOptions.find(
    (p) => p.toLowerCase() === rawPaidBy.toLowerCase()
  );
  const paidBy = validPaidBy || "";

  // Validate/match tax_category if present
  const rawTaxCategory = getVal("Tax Category").trim();
  let taxCategory: string | undefined = undefined;
  if (rawTaxCategory) {
    const matchedTaxCat = DEFAULT_TAX_CATEGORIES.find(
      (t) => t.toLowerCase() === rawTaxCategory.toLowerCase()
    );
    taxCategory = matchedTaxCat || rawTaxCategory;
  }

  return {
    data: {
      expense_date: rawDate || new Date().toISOString().split("T")[0],
      expense_name: getVal("Expense").trim(),
      vehicle_id: matchedVehicleId,
      expense_type: expenseType,
      tax_category: taxCategory,
      amount: rawAmount || 0,
      mileage: rawMileage || undefined,
      paid_by: paidBy || undefined,
      paid_to: getVal("Paid To").trim() || undefined,
      notes: getVal("Notes").trim() || undefined,
      
    },
    carValue: rawCarValue,
    needsNewVehicle: needsNewVehicle && rawCarValue !== "",
    matchedVehicleId,
  };
}
